export type WizardMode = 'onboarding' | 'connect';

export type WizardStepId =
  | 'welcome'
  | 'connect'
  | 'sync'
  | 'success'
  | 'selectFeed'
  | 'download'
  | 'ready';

/**
 * Ordered step ids for the Google account wizard.
 * - connect mode (adding another account from Settings): classic flow, ends in success.
 * - onboarding mode: extended with feed-select → download → ready.
 *   condensed = the user already has a connected google account (sync re-run only).
 */
export function getWizardStepIds(mode: WizardMode, condensed: boolean): WizardStepId[] {
  if (mode === 'connect') return ['welcome', 'connect', 'sync', 'success'];
  if (condensed) return ['sync', 'selectFeed', 'download', 'ready'];
  return ['welcome', 'connect', 'sync', 'selectFeed', 'download', 'ready'];
}

/** Returns the single item's id when a list has exactly one entry, else null. */
export function autoSelectId<T extends { id: string }>(items: T[]): string | null {
  return items.length === 1 ? items[0].id : null;
}

/**
 * Compute the next step (1-based) when advancing FROM a specific step id.
 *
 * Returns the step UNCHANGED if we're not currently on `fromId`. This guards
 * against stray/leftover advance callbacks — e.g. the @protected SyncProgressStep
 * schedules an uncleared `setTimeout(onNext, 1500)` that can survive its unmount and
 * fire while the user is already on the next step; without this guard that leftover
 * call would skip the feed-selection step straight to download.
 */
export function advanceFromStep(
  stepIds: WizardStepId[],
  currentStep: number,
  fromId: WizardStepId,
): number {
  if (stepIds[currentStep - 1] !== fromId) return currentStep;
  return Math.min(currentStep + 1, stepIds.length);
}
